// Library methods
import { forwardRef, useMemo } from "react";
import { useTranslation } from "react-i18next";

// MUI Components
import Typography from "@mui/material/Typography";
import Card from "@mui/material/Card";
import Grid from "@mui/material/Grid";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import { EditIcon } from "../../../../assets/icons";

// Utilities
import CustomCheckbox from "../../../../components/UI/CustomCheckbox";
import {
  getLocalDate,
  calculateYearDifference,
} from "../../../../utils/dateHelper";
import { getTruncatedText } from "../../../../utils/textHelper";
import useClinic from "../../../../hooks/useClinic";

const PatientCard = forwardRef(
  (
    {
      row,
      isSelected: isSelectedForDeletion,
      isActive,
      headCells,
      handleCheckboxClick,
      handleEditClick,
      handleCardClick,
    },
    ref
  ) => {
    // internationalization
    const { t } = useTranslation();
    const { clinicTimezone } = useClinic();

    const sexTitles = {
      Male: t("gender_male"),
      Female: t("gender_female"),
      Undefined: t("word_na"),
    };

    // Translate language property in the patient to specified language.
    const lang = row?.language
      ? "language_" +
        row.language.charAt(0).toLowerCase() +
        row.language.slice(1)
      : "-";

    const nextScheduledExamDate = useMemo(() => {
      if (row?.nextScheduledExamDate)
        return getLocalDate(row.nextScheduledExamDate, clinicTimezone);
      return "-";
    }, [row, clinicTimezone]);

    // truncated name
    const formattedPatientName = useMemo(
      () => getTruncatedText(`${row?.firstName} ${row?.lastName}`),
      [row]
    );

    const isSelectedForViewing = useMemo(
      () => isActive === row?.id,
      [isActive, row]
    );

    return (
      <Card
        elevation={0}
        key={row.id}
        sx={{
          mb: "16px",
          py: "8px",
          borderRadius: "8px",
          backgroundColor: isSelectedForViewing
            ? "#202338"
            : isSelectedForDeletion
              ? "#E2772E"
              : "#FFF",
          color:
            isSelectedForViewing || isSelectedForDeletion ? "#FFF" : "#202338",
          "&:hover": {
            backgroundColor: isSelectedForViewing
              ? "#202338"
              : isSelectedForDeletion
                ? "#CE631A"
                : "#5B6D7A",
            color: "#FFF",
          },
          boxShadow: "0px 4px 10px rgba(0, 0, 0, 0.15)",
          cursor: "pointer",
          height: "fit-content",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
        }}
        onClick={(event) => handleCardClick(event, row.id)}
        ref={ref}
      >
        <Grid
          container
          sx={{
            display: { xs: "grid", sm: "flex" },
            gridTemplateColumns: {
              xs: "10% 75% 10%",
              sm: "none",
            },
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <Grid item xs={1.5} sm={1}>
            <CustomCheckbox
              checkedFill={isSelectedForViewing ? "#FFF" : "#E2772E"}
              checkedStroke={isSelectedForViewing ? "#202338" : "#FFF"}
              checked={isSelectedForDeletion}
              onClick={(event) => {
                event.stopPropagation();
                handleCheckboxClick(event, row.id);
              }}
            />
          </Grid>
          <Grid
            container
            item
            xs={9.5}
            sm={10}
            display={{ sx: "block", sm: "flex" }}
          >
            {headCells.map((cell, index) => (
              <Grid item xs={12} sm={cell.gridSize} key={index}>
                <Typography
                  sx={{
                    display: { xs: "block", sm: "none" },
                    fontSize: {
                      xs: "12px",
                      sm: "12px",
                      md: "16px",
                      lg: "1rem",
                    },
                  }}
                >
                  <b>{cell.label}:</b>{" "}
                  {index === 0
                    ? formattedPatientName
                    : index === 1
                      ? row?.age ?? calculateYearDifference(row.dateOfBirth)
                      : index === 2
                        ? sexTitles[row.sex]
                        : index === 3
                          ? t(lang)
                          : nextScheduledExamDate}
                </Typography>
                <Typography
                  noWrap
                  sx={{
                    display: { xs: "none", sm: "block" },
                    fontSize: {
                      xs: "12px",
                      sm: "12px",
                      md: "16px",
                      lg: "1rem",
                    },
                    textAlign: "left",
                  }}
                >
                  {index === 0
                    ? formattedPatientName
                    : index === 1
                      ? row?.age ?? calculateYearDifference(row.dateOfBirth)
                      : index === 2
                        ? sexTitles[row.sex]
                        : index === 3
                          ? t(lang)
                          : nextScheduledExamDate}
                </Typography>
              </Grid>
            ))}
          </Grid>
          <Grid item xs={1}>
            <Tooltip title={t("patients_edit_modal_title")}>
              <span>
                <IconButton
                  size="medium"
                  sx={{
                    height: "40px",
                    color: "inherit",
                  }}
                  onClick={(event) => {
                    event.stopPropagation();
                    handleEditClick(row);
                  }}
                >
                  <EditIcon />
                </IconButton>
              </span>
            </Tooltip>
          </Grid>
        </Grid>
      </Card>
    );
  }
);

export default PatientCard;
